import { ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { QrCodeVideoComponent } from '../qr-code-video/qr-code-video.component';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { SharedService } from '../../services/shared.service';
import { mvnfbuAnimations } from '../animations';
import { VeNewsFeed } from '../../typings';

const ANIMATION_DELAY = 5000;
const IMAGE_TRANSITION_DELAY = 500;

@Component({
  selector: 'app-news',
  templateUrl: './news.component.html',
  styleUrl: './news.component.scss',
  standalone: false,
  animations: mvnfbuAnimations
})
export class NewsComponent implements OnDestroy {
  @ViewChild(QrCodeVideoComponent) qrCodeVideo: QrCodeVideoComponent;

  public isMobile = false;
  public currentNews: VeNewsFeed;
  public animationStateIn = false;
  public animationStateOut = false;
  public animationImageStateOut = false;
  public animationVideoStateFull = false;
  private newValue: VeNewsFeed;
  public animationVideoStateMove = false;
  private intervalID: ReturnType<typeof setInterval>;
  public nextImage: string = "";
  public currentImage: string = "";
  public currentImageStyle: string = "";
  public nextImageStyle: string = "";
  private currentImageIndex: number = 0;
  public displayVideo = false;
  public currentLanguage: string = "";
  private handleEndAction: boolean = true;

  private imageTimeoutId?: number;
  private videoTimeoutId?: number;
  private streamVideoTimeoutId?: number;
  private streamVideoStallTimeoutId?: number;
  private streamVideoRetryCount: number = 0;
  private readonly STREAM_VIDEO_MAX_RETRIES = 3;
  private readonly STREAM_VIDEO_STALL_TIMEOUT_MS = 10000;
  private langChangeSubscription?: Subscription;

  constructor(private translate: TranslateService, public sharedService: SharedService, private ref: ChangeDetectorRef) { 
    this.initializeLanguage();
    this.sharedService.getConfiguration();
  }

  private initializeLanguage(): void {
    this.currentLanguage = this.translate.currentLang;
    this.langChangeSubscription = this.translate.onLangChange
      .subscribe((langChangeEvent: LangChangeEvent) => {
        this.currentLanguage = langChangeEvent.lang;
    });
  }

  public clearTimeouts(): void {
    if (this.imageTimeoutId) {
      window.clearTimeout(this.imageTimeoutId);
      this.imageTimeoutId = undefined;
    }
    if (this.videoTimeoutId) {
      window.clearTimeout(this.videoTimeoutId);
      this.videoTimeoutId = undefined;
    }
    if (this.streamVideoTimeoutId) {
      window.clearTimeout(this.streamVideoTimeoutId);
      this.streamVideoTimeoutId = undefined;
    }
    if (this.streamVideoStallTimeoutId) {
      window.clearTimeout(this.streamVideoStallTimeoutId);
      this.streamVideoStallTimeoutId = undefined;
    }
    this.streamVideoRetryCount = 0;
    if (this.intervalID) {
      clearInterval(this.intervalID);
    }

    if(this.animationVideoStateFull){
      this.animationVideoStateFull = false;
      this.displayVideo = false;
    }

    // Prob.1 — Retirer les handlers DOM pour éviter les closures zombies
    this.cleanupVideoHandlers();
  }

  private cleanupVideoHandlers(): void {
    const video = document.getElementById('videoHTML') as HTMLVideoElement | null;
    if (!video) return;
    video.oncanplay = null;
    video.onplaying = null;
    video.onwaiting = null;
    video.onstalled = null;
    video.onerror   = null;
    video.onended   = null;
  }

  public setInitialValue(v: VeNewsFeed) {
    this.clearTimeouts();
    this.currentNews = v;
    this.handleMedias();
  }

  private displayNextImage() {
    this.imageTimeoutId = window.setTimeout(() => {
      this.animationImageStateOut = false;
      this.currentImageIndex = (this.currentImageIndex + 1) % this.currentNews.image.length;
      this.currentImage = this.currentNews.image[this.currentImageIndex];
      this.nextImage = this.currentNews.image[(this.currentImageIndex + 1) % this.currentNews.image.length];
    }, IMAGE_TRANSITION_DELAY);
  }

  private displayMultiImage() {
    this.currentImage = this.currentNews.image[0];
    this.currentImageIndex = 0;
    this.nextImage = this.currentNews.image[(this.currentImageIndex + 1) % this.currentNews.image.length];
    if (this.intervalID) {
      clearInterval(this.intervalID);
    }
    this.intervalID = setInterval(() => {
      this.animationImageStateOut = true;
      this.displayNextImage();
    }, ANIMATION_DELAY);
  }

  public changeValue(v: VeNewsFeed) {
    this.clearTimeouts();
    this.newValue = v;
    this.animationStateOut = true;
    this.handleEndAction = true;
  }

  public animOutDone() {
    if (this.handleEndAction && this.newValue) {
      this.currentNews = this.newValue;
      this.animationStateOut = false;
      this.animationStateIn = true;
      this.handleMedias();
    }
  }

  private handleMedias() {
    if (this.currentNews.image?.length > 1) {  
      this.displayMultiImage();
    }

    if (this.sharedService.isYoutubeVideo(this.currentNews) || this.sharedService.isStreamVideo(this.currentNews)) {
      console.log("is video");
      this.videoTimeoutId = window.setTimeout(() => {
        this.animationVideoStateFull = true;
        this.qrCodeVideo.setInitialValue(this.currentNews);
      }, ANIMATION_DELAY);
    }

    if (this.sharedService.isStreamVideo(this.currentNews)) {
      this.displayStreamVideo();
    }
    this.handleEndAction = false;
  }

  public displayStreamVideo() {
    this.streamVideoRetryCount = 0;
    this.streamVideoTimeoutId = window.setTimeout(() => {
      this.loadStreamVideo();
    }, ANIMATION_DELAY);
  }

  private loadStreamVideo(): void {
    const video: HTMLVideoElement | null = document.getElementById('videoHTML') as HTMLVideoElement;
    if (!video) return;

    const clearStallTimeout = () => {
      if (this.streamVideoStallTimeoutId) {
        window.clearTimeout(this.streamVideoStallTimeoutId);
        this.streamVideoStallTimeoutId = undefined;
      }
    };

    const retryOrClose = () => {
      clearStallTimeout();
      if (this.streamVideoRetryCount < this.STREAM_VIDEO_MAX_RETRIES) {
        this.streamVideoRetryCount++;
        console.warn(`Stream video connection lost – retry ${this.streamVideoRetryCount}/${this.STREAM_VIDEO_MAX_RETRIES}`);
        video.pause();
        video.removeAttribute('src');
        // Prob.2 — Ne pas appeler video.load() sans src : déclenche onstalled et épuise les retries
        // Short delay before reloading the source
        this.streamVideoStallTimeoutId = window.setTimeout(() => {
          video.src = this.currentNews.videoUrl + '?t=' + new Date().getTime();
          video.load();
        }, 2000);
      } else {
        console.error('Stream video max retries reached – moving to next news');
        this.closeAnimation();
      }
    };

    const setStallTimeout = () => {
      clearStallTimeout();
      this.streamVideoStallTimeoutId = window.setTimeout(() => {
        retryOrClose();
      }, this.STREAM_VIDEO_STALL_TIMEOUT_MS);
    };

    video.muted = true;
    video.pause();
    video.src = this.currentNews.videoUrl + '?t=' + new Date().getTime();

    video.oncanplay = () => {
      clearStallTimeout();
      video.play().catch(err => {
        console.error('Autoplay blocked:', err);
        retryOrClose();
      });
    };

    video.onplaying = () => {
      clearStallTimeout();
    };

    // Fired when playback stalls mid-stream (network interruption)
    video.onwaiting = () => {
      setStallTimeout();
    };

    video.onstalled = () => {
      retryOrClose();
    };

    video.onerror = () => {
      console.error('Stream video error:', video.error);
      retryOrClose();
    };

    video.onended = () => {
      clearStallTimeout();
      this.closeAnimation();
    };

    // Prob.3 — Démarrer le timeout avant load() : couvre le cas où le réseau est absent
    // dès le départ et ne déclenche jamais waiting/stalled/canplay
    setStallTimeout();
    video.load();
  }

  public animInDone() {
    this.animationStateIn = false;
    this.handleEndAction = true;
  }

  public animMoveDone() {
    if (this.animationVideoStateFull) {
      this.displayVideo = true;
    }
  }

  public closeAnimation() {
    this.animationVideoStateFull = false;
    this.displayVideo = false;
    this.sharedService.emitVideoEnded();
  }

  public youtubeClose(close: boolean) {
    this.closeAnimation();
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
  }
}